import { useContext, useState } from 'react';
import { Button } from 'react-bootstrap';
import { useHistory } from 'react-router';
import styled, { useTheme } from 'styled-components';

import { AppContext } from '../../../context/AppContext';
import LoopSpinner from '../../../ui/LoopSpinner';
import InfoModal from '../../../ui/Modal/InfoModal';
import { StyledWarningBox } from '../../../ui/Styled';

const StyledEmBox = styled.p`
  font-style: ${(props) => (props.theme.standardLayout.shouldTextsBeItalic.tariffRecommender ? 'italic' : 'normal')};
  background-color: ${(props) => props.theme.colors.pale};
  padding: 16px;
  border-radius: 4px;
`;

const StyledButton = styled(Button)`
  margin-bottom: 0;
`;

const WelcomeContainer = () => {
  const theme = useTheme();
  const history = useHistory();
  const { recommendedTariffs, loadingRecommendedTariffs } = useContext(AppContext);
  const [showModal, setShowModal] = useState(false);

  const InfoContent = (
    <p>
      Smart tariffs are more complicated to compare than standard tariffs. Prices can change every 30 minutes and{' '}
      {theme.language.appName} simulates how your battery would have behaved with each tariff. This involves deciding
      when to charge and discharge and by how much for a year's data. This optimised behaviour can turn a costly tariff
      into a lucrative one - and that's what the tariff recommender is working out for you. It takes a little time, but
      your recommendation should be ready soon.
    </p>
  );

  return (
    <>
      <InfoModal show={showModal} onClose={() => setShowModal(false)} infoContent={InfoContent} />
      <div className="pt-4">
        <div className="d-block d-sm-flex align-items-start">
          <div>
            <h3 className="pb-1">Welcome to the {theme.language.appName} tariff recommender</h3>
            <p>
              Picking a time-of-use tariff can be complicated. We've simplified this process by using your data and
              simulating how much you would have paid on different types of tariffs over the last 12 months, so that you
              can make an informed decision about which tariff to be on.
            </p>
            <p className="pb-sm-3 pb-0">
              Note the figures shown for each tariff do not yet currently include standing charge information.
            </p>
          </div>
          <div className="d-flex align-items-center justify-content-center">
            <img
              src={theme.images.tariffRecommender.mainCardLogo}
              alt="Tariff Recommender Illustration"
              className="pl-sm-4 pl-0 pb-4"
              style={{ maxHeight: '200px' }}
            />
          </div>
        </div>
        <StyledEmBox>
          {theme.language.appName} is not a switching site, it's a guide to inform you what the best tariff is for you.
          It's up to you to switch. Once your switch completes, update your tariff in Settings and{' '}
          {theme.language.appName} will continue to work its magic.
        </StyledEmBox>
        {loadingRecommendedTariffs ? (
          <p className="p-0 m-0">
            <LoopSpinner />
          </p>
        ) : (
          <>
            {!!recommendedTariffs?.length ? (
              <StyledButton onClick={() => history.push('/app/tariffs/recommended')} block>
                View your recommended tariffs
              </StyledButton>
            ) : (
              <StyledWarningBox>
                <h4 className="d-flex align-items-center">
                  <img src={theme.icons.info} alt="Info" className="pr-2" /> We're working on which tariffs to recommend
                  to you.
                </h4>
                <p className="mb-3">We'll let you know when the tariffs are ready.</p>
                <Button variant="link" onClick={() => setShowModal(true)} className="p-0">
                  What does this mean?
                </Button>
              </StyledWarningBox>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default WelcomeContainer;
